/** @author PVCOMBANK */
import * as React from "react";
import { Paragraph, Switch, TextInput, useTheme } from "react-native-paper";
import { RegisterOptions, useForm } from "react-hook-form";
import { Button, ActivityIndicator } from "react-native-paper";
import { Block } from "@block";
import { useDesktopMediaQuery, useMobileMediaQuery } from "@media-query";
import { useTranslation } from "react-i18next";
import { spacing } from "@styles/spacing";
import { QueueSpacing, StackSpacing } from "@components/spacing";
import { InputText } from "app/shared/components/form/input";
import { StyleSheet } from "react-native";
import { CARDDETAIL, CARDITEM } from "app/shared/services/services-model.type";
import { formatNumberThousand } from "app/shared/util/format-currency";
import { ArrayCreditShow, ArrayDebitShowHistory, getHotStatus, getTitleCard } from "../../card-utils/card-utils";
import { useDispatch, useSelector } from "react-redux";
import {
  getCardFullNumSelector,
  getCardStoreSelector,
  getLimit4NumDigitSelector,
  getResponseCardDetailSelector,
  getStatusCardDetailSelector,
} from "../../reducer/selectors";
import { Constant } from "app/shared/constant/constant";
import { setCardStore, setLimit4NumDigit } from "../../reducer/actions";
import { ParamCardRequest, SWITCH_ACTION } from "../list-card.web";
import { showModal } from "app/modules/modal-management/reducer/action";
import ModalActivatedCard from "./modal-input-card-activated";
import { Limit4NumberDigit } from "../../reducer/types";

export interface CardDetailProps {
  card?: CARDITEM;
  actionClick: Function;
  updateListCard: Function;
  onCancel: Function;
}

enum FormEnum {
  cardNum,
  embossName,
  availBalance,
  cardType,
  issueDate,
  expireDate,
  eComStatus,
  hotStatus,
  email,
  phoneNum,
  exesLimit,
  holdBalance,
  totalAmount,
  mandatoryAmount,
  dueDate,
  maxDailyCashLimit,
  maxDailyEComLimit,
  supplCardNum,
  supplCardBossName,
  supplCardExesLimit,
  supplCardHotStatus,
}

type ValidDataSchemeFormData = {
  [key in keyof typeof FormEnum]: Omit<RegisterOptions, "valueAsNumber" | "valueAsDate" | "setValueAs">;
};

interface ItemSwitchProps {
  index: number;
  title: string;
  value: boolean;
  display?: any;
}

export type CardDetailData = {
  cardNum: string;
  embossName: string;
  cardType: string;
  availBalance: string;
  issueDate: string;
  expireDate: string;
  email: string;
  phoneNum: string;
  eComStatus: string;
  hotStatus: string;
  // Thẻ tín dụng
  exesLimit: string;
  holdBalance: string;
  totalAmount: string;
  mandatoryAmount: string;
  dueDate: string;
  maxDailyCashLimit: string;
  maxDailyEComLimit: string;
  supplCardNum: string;
  supplCardBossName: string;
  supplCardExesLimit: string;
  supplCardHotStatus: string;
};

const CardDetail = (props: CardDetailProps) => {
  const { card, actionClick, updateListCard } = props;
  const { colors, fonts } = useTheme();
  const { t } = useTranslation();
  const isDesktop = useDesktopMediaQuery();
  const isMobile = useMobileMediaQuery();
  const dispatchStore = useDispatch();

  const cardStore = useSelector(getCardStoreSelector);
  const cardDetailStore = useSelector(getResponseCardDetailSelector);
  const cardDetailStatus = useSelector(getStatusCardDetailSelector);
  const cardFullNumStore = useSelector(getCardFullNumSelector);
  const limit4NumDigitStore = useSelector(getLimit4NumDigitSelector);

  const [isSwitchActivated, setIsSwitchActivated] = React.useState<boolean>(false);
  const [isSwitchHostStatus, setIsSwitchHostStatus] = React.useState<boolean>(false);
  const [isSwitchEComStatus, setIsSwitchEComStatus] = React.useState<boolean>(false);
  const [cardDetail, setCardDetail] = React.useState<CARDDETAIL | undefined>();
  const [showCardNum, setShowCardNum] = React.useState(false);
  const [hotStatus, setHotStatus] = React.useState<string | undefined>(card?.hotStatus);
  const [showDialog, setShowDialog] = React.useState<boolean>(false);
  const [showDialogMulti, setShowDialogMulti] = React.useState<boolean>(false);
  const [dialogMessage, setDialogMessage] = React.useState<string>("");
  const [actionIndex, setActionIndex] = React.useState<number>(-1);
  const [showModalActivated, setShowModalActivated] = React.useState<boolean>(false);

  // Instance Form Method
  const { control, setValue, getValues } = useForm<CardDetailData>({
    mode: "onChange",
  });

  // Styles Component
  const styles = React.useMemo(
    () =>
      StyleSheet.create({
        fieldFromText: {
          textAlign: "left",
          color: colors.text,
          width: 200,
          ...fonts.semibold,
        },
        text: { ...fonts.semibold, color: colors.text },
        buttonContainer: {
          alignSelf: "center",
          backgroundColor: colors.primary,
        },
        buttonCredit: {
          display: ArrayCreditShow.includes(hotStatus || "") && card?.cardType === Constant.CARD.CARD_TYPE.CREDIT ? "flex" : "none",
        },
        buttonHidden: {
          display:
            (card?.cardType === Constant.CARD.CARD_TYPE.CREDIT && ArrayCreditShow.includes(hotStatus || "")) ||
            ((card?.cardType === Constant.CARD.CARD_TYPE.DEBIT || card?.cardType === Constant.CARD.CARD_TYPE.DEBIT_MASTER) &&
              ArrayDebitShowHistory.includes(hotStatus || ""))
              ? "flex"
              : "none",
        },
        iconEye: { top: spacing.medium, display: hotStatus === "0" || hotStatus === "20" ? "flex" : "none" },
        fab: { position: "absolute", margin: 16, right: 0, bottom: 0 },
      }),
    [colors.text, colors.primary, fonts.semibold, hotStatus, card?.cardType],
  );

  const getDateStatement = React.useCallback(
    (date?: string) => {
      if (date?.length) {
        let text = t("CARD_TEXT_NGAY_SAO_KE").replace("[date]", date);
        return text;
      }
      return "";
    },
    [t],
  );

  const checkLimitInput4NumDigit = React.useCallback(
    (cardNum: string) => {
      if (limit4NumDigitStore && limit4NumDigitStore.length) {
        const filters = limit4NumDigitStore.filter((item) => {
          return item.cardID === card?.cardId && item.limit === 0;
        });
        if (filters.length) {
          setDialogMessage(t("CARD_THONG_BAO_LOI_KICH_HOAT_THE_LIMIT"));
          setShowDialog(true);
        } else {
          setDialogMessage(t("CARD_THONG_BAO_HOI_KICH_HOAT_THE").replace("[card_num]", cardNum));
          setShowDialogMulti(true);
        }
      }
    },
    [card?.cardId, limit4NumDigitStore, t],
  );

  const handlerAction = React.useCallback(
    (index: number) => {
      setActionIndex(index);
      const cardNum = cardFullNumStore?.cardNum && showCardNum ? cardFullNumStore?.cardNum : card?.cardNum ? card?.cardNum : "";
      switch (index) {
        case SWITCH_ACTION.ACTIVATED_CARD:
          checkLimitInput4NumDigit(cardNum);
          break;
        case SWITCH_ACTION.HOT_STATUS:
        case SWITCH_ACTION.E_COM_STATUS:
          let statusUpdate: boolean = false;
          if (index === SWITCH_ACTION.HOT_STATUS) {
            statusUpdate = card?.hotStatus === "20" ? true : false;
            let message = statusUpdate
              ? t("CARD_THONG_BAO_HOI_MO_THE").replace("[card_num]", cardNum)
              : t("CARD_THONG_BAO_HOI_KHOA_THE").replace("[card_num]", cardNum);
            setDialogMessage(message);
            setShowDialogMulti(true);
          }
          if (index === SWITCH_ACTION.E_COM_STATUS) {
            statusUpdate = card?.ecomStatus === true ? false : true;
            let message = statusUpdate
              ? t("CARD_THONG_BAO_KICH_HOAT_GIAO_DICH_TRUC_TUYEN").replace("[card_num]", cardNum)
              : t("CARD_THONG_BAO_HOI_KHOA_GIAO_DICH_TRUC_TUYEN").replace("[card_num]", cardNum);
            setDialogMessage(message);
            setShowDialogMulti(true);
          }
          break;
        case SWITCH_ACTION.SHOW_FULL_NUM:
          if (showCardNum) {
            dispatchStore(setCardStore({ ...cardStore, cardNumFull: undefined }));
          } else {
            const paramShowFullNum: ParamCardRequest = {
              cardID: card?.cardId || "",
            };
            actionClick(index, paramShowFullNum);
          }
          break;
        case SWITCH_ACTION.PAYMENT_CARD_STATEMENT:
        case SWITCH_ACTION.CARD_HISTORY:
          const paramNavigate: ParamCardRequest = {
            cardID: card?.cardId || "",
          };
          actionClick(index, paramNavigate);
          break;
        case SWITCH_ACTION.PAYMENT_CARD:
        case SWITCH_ACTION.PHYSICAL_REGISTER:
        case SWITCH_ACTION.GET_LIST_CARD:
          actionClick(index);
          break;
        case SWITCH_ACTION.GET_DETAIL:
          const paramGetDetail: ParamCardRequest = {
            cardID: card?.cardId || "",
          };
          actionClick(index, paramGetDetail);
          break;
        default:
          break;
      }
    },
    [
      actionClick,
      card?.cardId,
      card?.cardNum,
      card?.ecomStatus,
      card?.hotStatus,
      cardFullNumStore?.cardNum,
      cardStore,
      checkLimitInput4NumDigit,
      dispatchStore,
      showCardNum,
      t,
    ],
  );

  const handlerAcceptModal = React.useCallback(() => {
    switch (actionIndex) {
      case SWITCH_ACTION.ACTIVATED_CARD:
        setActionIndex(SWITCH_ACTION.ACTIVATED_ACCEPT);
        setShowModalActivated(true);
        // const paramActivated: ParamCardRequest = {
        //   cardID: card?.cardId || "",
        //   // suffix: value,
        // };
        // actionClick(SWITCH_ACTION.ACTIVATED_ACCEPT, paramActivated);
        break;
      case SWITCH_ACTION.HOT_STATUS:
      case SWITCH_ACTION.E_COM_STATUS:
        let statusUpdate: boolean = false;
        if (actionIndex === SWITCH_ACTION.HOT_STATUS) statusUpdate = card?.hotStatus === "20" ? true : false;
        if (actionIndex === SWITCH_ACTION.E_COM_STATUS) statusUpdate = card?.ecomStatus === true ? false : true;
        const paramUpdateStatus: ParamCardRequest = {
          cardID: card?.cardId || "",
          status: statusUpdate,
        };
        actionClick(actionIndex, paramUpdateStatus);
        break;
      default:
        break;
    }
  }, [actionClick, actionIndex, card?.cardId, card?.ecomStatus, card?.hotStatus]);

  const handlerAcceptModalActivated = React.useCallback(
    (value: string) => {
      const paramActivated: ParamCardRequest = {
        cardID: card?.cardId || "",
        suffix: value,
      };
      actionClick(SWITCH_ACTION.ACTIVATED_ACCEPT, paramActivated);
    },
    [actionClick, card?.cardId],
  );

  /// MARK: - Child Component

  const rightCurrency = <TextInput.Affix textStyle={fonts.regular} text={t("COMMON_VND")} />;
  const rightShowHidden = (
    <TextInput.Icon
      name={showCardNum ? "eye-off" : "eye"}
      size={20}
      style={styles.iconEye}
      onPress={() => handlerAction(SWITCH_ACTION.SHOW_FULL_NUM)}
    />
  );

  const ItemSwitch = (item: ItemSwitchProps) => {
    return (
      <Block row spaced center key={item.index} display={item.display}>
        <Paragraph style={styles.text}>{item.title}</Paragraph>
        <Switch value={item.value} onValueChange={() => handlerAction(item.index)} />
      </Block>
    );
  };

  const FormCard = (
    <Block>
      <Block spaced={isDesktop} row={isDesktop}>
        <Block flex={1}>
          <InputText control={control} inputProps={{ editable: false }} name="cardType" label={t("CARD_LOAI_THE")} />
        </Block>
        <QueueSpacing size="huge" />
        <Block flex={1}>
          <InputText control={control} inputProps={{ editable: false, right: rightShowHidden }} name="cardNum" label={t("CARD_SO_THE")} />
        </Block>
      </Block>
      <Block spaced={isDesktop} row={isDesktop}>
        <Block flex={1}>
          <InputText
            control={control}
            inputProps={{ editable: false, right: rightCurrency }}
            name="availBalance"
            label={t("CARD_SO_DU_KHA_DUNG")}
          />
        </Block>
        <QueueSpacing size="huge" />
        <Block flex={1}>
          <InputText control={control} inputProps={{ editable: false }} name="hotStatus" label={t("CARD_TRANG_THAI")} />
        </Block>
      </Block>
      {cardDetail ? (
        <Block spaced={isDesktop} row={isDesktop}>
          <Block flex={1}>
            <InputText control={control} inputProps={{ editable: false }} name="phoneNum" label={t("CARD_SO_DIEN_THOAI")} />
          </Block>
          <QueueSpacing size="huge" />
          <Block flex={1}>
            {isDesktop || cardDetail?.email?.length ? (
              <InputText control={control} inputProps={{ editable: false }} name="email" label={t("CARD_EMAIL")} />
            ) : (
              <></>
            )}
          </Block>
        </Block>
      ) : (
        <></>
      )}
    </Block>
  );

  const FormCardCredit =
    cardDetail?.cardType === Constant.CARD.CARD_TYPE.CREDIT ? (
      <>
        <Block spaced={isDesktop} row={isDesktop}>
          <Block flex={1}>
            <InputText
              control={control}
              inputProps={{ editable: false, right: rightCurrency }}
              name="exesLimit"
              label={t("CARD_HAN_MUC_THE")}
            />
          </Block>
          <QueueSpacing size="huge" />
          <Block flex={1}>
            <InputText
              control={control}
              inputProps={{ editable: false, right: rightCurrency }}
              name="holdBalance"
              label={t("CARD_DU_NO")}
            />
          </Block>
        </Block>
        <Block spaced={isDesktop} row={isDesktop}>
          <Block flex={1}>
            <InputText control={control} inputProps={{ editable: false }} name="dueDate" label={t("CARD_NGAY_SAO_KE")} />
          </Block>
          <QueueSpacing size="huge" />
          <Block flex={1}>
            <InputText
              control={control}
              inputProps={{ editable: false, right: rightCurrency }}
              name="mandatoryAmount"
              label={t("CARD_THANH_TOAN_THE_TIN_DUNG_THANH_TOAN_TOI_THIEU")}
            />
          </Block>
        </Block>
      </>
    ) : (
      <></>
    );

  const FormDailyLimit = cardDetail?.cardId ? (
    <Block>
      <Block spaced={isDesktop} row={isDesktop}>
        <Block flex={1}>
          <InputText
            control={control}
            inputProps={{ editable: false, right: rightCurrency }}
            name="maxDailyCashLimit"
            label={t("CARD_HAN_MUC_GIAO_DICH_RUT_ATM")}
          />
        </Block>
        <QueueSpacing size="huge" />
        <Block flex={1}>
          <InputText
            control={control}
            inputProps={{ editable: false, right: rightCurrency }}
            name="maxDailyEComLimit"
            label={
              cardDetail?.cardType === Constant.CARD.CARD_TYPE.DEBIT || cardDetail?.cardType === Constant.CARD.CARD_TYPE.DEBIT_MASTER
                ? t("CARD_HAN_MUC_GIAO_DICH_CHUYEN_TIEN")
                : t("CARD_HAN_MUC_GIAO_DICH_TRUC_TUYEN")
            }
          />
        </Block>
      </Block>
    </Block>
  ) : (
    <></>
  );

  const FormSwitch =
    hotStatus === "1" ? (
      <Block spaced={isDesktop} row={isDesktop}>
        <Block flex={1} ph={isDesktop ? spacing.medium : 0} pv={spacing.small}>
          <ItemSwitch index={SWITCH_ACTION.ACTIVATED_CARD} title={t("CARD_KICH_HOAT_THE")} value={isSwitchActivated} display="flex" />
        </Block>
        <QueueSpacing size="huge" />
        <Block flex={1} ph={isDesktop ? spacing.medium : 0} pv={spacing.small} />
      </Block>
    ) : (
      <Block spaced={isDesktop} row={isDesktop}>
        <Block flex={1} ph={isDesktop ? spacing.medium : 0} pv={spacing.small}>
          <ItemSwitch
            index={SWITCH_ACTION.HOT_STATUS}
            title={t("CARD_KHOA_MO_THE")}
            value={isSwitchHostStatus}
            display={hotStatus === "20" || hotStatus === "0" ? "flex" : "none"}
          />
        </Block>
        <QueueSpacing size="huge" />
        <Block flex={1} ph={isDesktop ? spacing.medium : 0} pv={spacing.small}>
          <ItemSwitch
            index={SWITCH_ACTION.E_COM_STATUS}
            title={t("CARD_KHOA_MO_GIAO_DICH_TRUC_TUYEN")}
            value={isSwitchEComStatus}
            display={hotStatus === "0" ? "flex" : "none"}
          />
        </Block>
      </Block>
    );

  const ButtonForm = (
    <Block row={!isMobile} center middle pv={spacing.huge}>
      <>
        <Button
          mode="contained"
          style={[styles.buttonContainer, styles.buttonCredit]}
          onPress={() => handlerAction(SWITCH_ACTION.PAYMENT_CARD)}>
          {t("CARD_THANH_TOAN_THE")}
        </Button>
        <QueueSpacing size="large" />
        <StackSpacing size="medium" />
        <Button
          mode="contained"
          style={[styles.buttonContainer, styles.buttonCredit]}
          onPress={() => handlerAction(SWITCH_ACTION.PAYMENT_CARD_STATEMENT)}>
          {t("CARD_SAO_KE_THE")}
        </Button>
        <QueueSpacing size={card?.cardType === Constant.CARD.CARD_TYPE.CREDIT ? "large" : "none"} />
        <StackSpacing size={card?.cardType === Constant.CARD.CARD_TYPE.CREDIT ? "medium" : "none"} />
      </>
      <Button
        mode="contained"
        style={[styles.buttonContainer, styles.buttonHidden]}
        onPress={() => handlerAction(SWITCH_ACTION.CARD_HISTORY)}>
        {t("CARD_LICH_SU_GIAO_DICH")}
      </Button>
      <QueueSpacing
        size={
          (card?.cardType === Constant.CARD.CARD_TYPE.CREDIT && ArrayCreditShow.includes(hotStatus || "")) ||
          ((card?.cardType === Constant.CARD.CARD_TYPE.DEBIT || card?.cardType === Constant.CARD.CARD_TYPE.DEBIT_MASTER) &&
            ArrayDebitShowHistory.includes(hotStatus || ""))
            ? "large"
            : "none"
        }
      />
      <StackSpacing
        size={
          (card?.cardType === Constant.CARD.CARD_TYPE.CREDIT && ArrayCreditShow.includes(hotStatus || "")) ||
          ((card?.cardType === Constant.CARD.CARD_TYPE.DEBIT || card?.cardType === Constant.CARD.CARD_TYPE.DEBIT_MASTER) &&
            ArrayDebitShowHistory.includes(hotStatus || ""))
            ? "medium"
            : "none"
        }
      />
      <Button mode="contained" style={styles.buttonContainer} onPress={() => handlerAction(SWITCH_ACTION.PHYSICAL_REGISTER)}>
        {t("CARD_DANG_KY_PHAT_HANH_THE_VAT_LY")}
      </Button>
    </Block>
  );

  React.useEffect(() => {
    if (cardDetail?.cardId === card?.cardId) {
      setValue("cardNum", cardDetail?.cardNum || "");
      setValue("availBalance", formatNumberThousand(cardDetail?.avalBalance));
      setValue("hotStatus", getHotStatus(cardDetail?.hotStatus));
      setValue("phoneNum", cardDetail?.phoneNum || "");
      setValue("email", cardDetail?.email || "");
      setValue("maxDailyCashLimit", formatNumberThousand(cardDetail?.cardDailyLimit?.minDailyCashLimit));
      setValue("maxDailyEComLimit", formatNumberThousand(cardDetail?.cardDailyLimit?.minDailyEcomLimit));
      if (cardDetail?.cardType === Constant.CARD.CARD_TYPE.CREDIT) {
        setValue("exesLimit", formatNumberThousand(cardDetail?.exesLimit));
        setValue("holdBalance", formatNumberThousand(cardDetail?.holdBalance));
        setValue("mandatoryAmount", formatNumberThousand(cardDetail?.mandatoryAmount));
        setValue("dueDate", getDateStatement(cardDetail?.duedate));
      }
      setHotStatus(cardDetail?.hotStatus);
    }
  }, [card, cardDetail, cardDetail?.cardNum, cardDetail?.hotStatus, cardDetail?.ecomStatus, getDateStatement, setValue, getValues]);

  React.useEffect(() => {
    if (card && !cardDetail?.cardId) {
      setValue("cardNum", card?.cardNum || "");
      setValue("embossName", card?.embossName || "");
      setValue("availBalance", formatNumberThousand(card?.avalBalance));
      let noteSupplCard = "";
      if (card.primaryCard === false) {
        noteSupplCard = " ( " + t("CARD_THE_PHU") + " )";
      }
      setValue("cardType", getTitleCard(card?.cardType) + noteSupplCard);
      setValue("hotStatus", getHotStatus(card?.hotStatus));
      setValue("maxDailyCashLimit", "");
      setValue("maxDailyEComLimit", "");
      if (card.cardType === Constant.CARD.CARD_TYPE.CREDIT) {
        setValue("exesLimit", "");
        setValue("holdBalance", "");
        setValue("mandatoryAmount", "");
        setValue("dueDate", "");
      }
      setHotStatus(card?.hotStatus);
    }
  }, [card, card?.hotStatus, card?.ecomStatus, setValue, cardDetail, t]);

  React.useEffect(() => {
    const _card = cardDetail?.cardId ? cardDetail : card;
    if (_card?.cardId) {
      if (_card?.hotStatus === "1" && isSwitchActivated) {
        setIsSwitchActivated(false);
      } else if (_card?.hotStatus !== "1" && !isSwitchActivated) {
        setIsSwitchActivated(true);
      }
    }
  }, [isSwitchActivated, card, cardDetail, card?.hotStatus]);

  React.useEffect(() => {
    const _card = cardDetail?.cardId ? cardDetail : card;
    if (_card?.cardId) {
      if (_card?.hotStatus === "20" && isSwitchHostStatus) {
        setIsSwitchHostStatus(false);
      } else if (_card?.hotStatus === "0" && !isSwitchHostStatus) {
        setIsSwitchHostStatus(true);
      }
    }
  }, [isSwitchHostStatus, card, cardDetail, card?.hotStatus]);

  React.useEffect(() => {
    const _card = cardDetail?.cardId ? cardDetail : card;
    if (_card?.cardId) {
      if (_card?.ecomStatus === true && !isSwitchEComStatus) {
        setIsSwitchEComStatus(true);
      } else if (_card?.ecomStatus === false && isSwitchEComStatus) {
        setIsSwitchEComStatus(false);
      }
    }
  }, [isSwitchEComStatus, card, cardDetail, card?.ecomStatus]);

  React.useEffect(() => {
    if (cardDetailStore?.cardId === card?.cardId) {
      console.log("setCardDetail: ", cardDetailStore);
      setCardDetail(cardDetailStore);
    }
  }, [card, cardDetailStore]);

  React.useEffect(() => {
    if (cardFullNumStore?.cardNum?.length && card?.cardId === cardFullNumStore.cardID) {
      setShowCardNum(true);
      setValue("cardNum", cardFullNumStore.cardNum);
    } else {
      setShowCardNum(false);
      setValue("cardNum", cardDetail?.cardNum ? cardDetail?.cardNum : card?.cardNum || "");
    }
  }, [card, cardDetail, cardStore, cardFullNumStore, setValue, dispatchStore]);

  React.useEffect(() => {
    if (
      (cardDetail?.cardId?.length && cardDetail?.cardId !== card?.cardId) ||
      (cardFullNumStore?.cardID?.length && cardFullNumStore?.cardID !== card?.cardId)
    ) {
      setCardDetail(undefined);
      dispatchStore(
        setCardStore({
          ...cardStore,
          resCardDetail: undefined,
          cardNumFull: undefined,
        }),
      );
    }
  }, [card, cardDetail?.cardId, cardFullNumStore?.cardID, cardStore, dispatchStore]);

  React.useEffect(() => {
    if (cardStore.statusCardAuth === "ok") {
      if (
        actionIndex === SWITCH_ACTION.ACTIVATED_ACCEPT ||
        actionIndex === SWITCH_ACTION.HOT_STATUS ||
        actionIndex === SWITCH_ACTION.E_COM_STATUS
      ) {
        let message = "";
        const cardNum = cardFullNumStore?.cardNum && showCardNum ? cardFullNumStore?.cardNum : card?.cardNum ? card?.cardNum : "";
        if (actionIndex === SWITCH_ACTION.ACTIVATED_ACCEPT) {
          message =
            card?.cardType === Constant.CARD.CARD_TYPE.CREDIT
              ? t("CARD_THONG_BAO_KICH_HOAT_THE_CREDIT_THANH_CONG").replace("[card_num]", cardNum)
              : t("CARD_THONG_BAO_KICH_HOAT_THE_DEBIT_THANH_CONG").replace("card_num", cardNum);
        } else if (actionIndex === SWITCH_ACTION.HOT_STATUS) {
          message = isSwitchHostStatus
            ? t("CARD_THONG_BAO_KHOA_THE_THANH_CONG").replace("[card_num]", cardNum)
            : t("CARD_THONG_BAO_MO_KHOA_THE_THANH_CONG").replace("[card_num]", cardNum);
        } else if (actionIndex === SWITCH_ACTION.E_COM_STATUS) {
          message = isSwitchEComStatus
            ? t("CARD_THONG_BAO_KHOA_GIAO_DICH_ONLINE_THANH_CONG").replace("[card_num]", cardNum)
            : t("CARD_THONG_BAO_KICH_HOAT_GIAO_DICH_TRUC_TUYEN_THANH_CONG").replace("[card_num]", cardNum);
        }
        setDialogMessage(message);
        setShowDialog(true);
        if (actionIndex === SWITCH_ACTION.ACTIVATED_ACCEPT) {
          if (cardStore?.limit4NumDigit && cardStore.limit4NumDigit.length) {
            let arrayLimit: Limit4NumberDigit[] = cardStore.limit4NumDigit;
            let i = -1;
            cardStore.limit4NumDigit.filter((item, index) => {
              if (item.cardID === card?.cardId) i = index;
              return item;
            });
            if (i >= 0) arrayLimit = cardStore.limit4NumDigit.splice(i, 1);
            dispatchStore(setLimit4NumDigit(arrayLimit));
          }
        }
        dispatchStore(setCardStore({ ...cardStore, statusCardConfirm: "init", statusCardAuth: "init" }));
        updateListCard();
      } else if (actionIndex === SWITCH_ACTION.SHOW_FULL_NUM) {
        dispatchStore(
          setCardStore({
            ...cardStore,
            statusCardConfirm: "init",
            statusCardAuth: "init",
            cardNumFull: {
              cardID: card?.cardId,
              cardNum: cardStore.resCardAuth?.cardNum,
            },
          }),
        );
      }
    }
  }, [
    actionIndex,
    card?.cardId,
    card?.cardNum,
    card?.cardType,
    cardFullNumStore?.cardNum,
    cardStore,
    dispatchStore,
    isSwitchEComStatus,
    isSwitchHostStatus,
    showCardNum,
    t,
    updateListCard,
  ]);

  React.useEffect(() => {
    if (showDialog && dialogMessage.length) {
      setShowDialog(false);
      dispatchStore(
        showModal({
          modalType: "alert",
          modalProps: {
            open: true,
            message: dialogMessage,
          },
        }),
      );
    }
  }, [dialogMessage, dispatchStore, showDialog]);

  React.useEffect(() => {
    if (showDialogMulti && dialogMessage.length) {
      setShowDialogMulti(false);
      dispatchStore(
        showModal({
          modalType: "multiAction",
          modalProps: {
            open: true,
            message: dialogMessage,
            acceptModal: handlerAcceptModal,
          },
        }),
      );
    }
  }, [dialogMessage, dispatchStore, handlerAcceptModal, showDialogMulti]);

  React.useEffect(() => {
    let dataNew: Limit4NumberDigit[] = [];
    if (cardStore.statusCardConfirm === "error" && actionIndex === SWITCH_ACTION.ACTIVATED_ACCEPT) {
      if (limit4NumDigitStore?.length) {
        dataNew = limit4NumDigitStore.map((item) => {
          if (item.cardID === card?.cardId) {
            item.limit -= 1;
          }
          return item;
        });
      }
      // console.log("setLimit4NumDigit dataNew error: ", dataNew);
      dispatchStore(setLimit4NumDigit(dataNew));
    } else if (cardStore.statusCardConfirm === "ok" && actionIndex === SWITCH_ACTION.ACTIVATED_ACCEPT) {
      if (limit4NumDigitStore?.length) {
        dataNew = limit4NumDigitStore.map((item) => {
          if (item.cardID === card?.cardId) {
            item.limit = 3;
          }
          return item;
        });
      }
      // console.log("setLimit4NumDigit dataNew ok: ", dataNew);
      dispatchStore(setLimit4NumDigit(dataNew));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardStore.statusCardConfirm, dispatchStore]);

  return (
    <>
      <Block pt={spacing.medium}>
        <Block flex={1} bg={colors.backgroundForm} br={12} p={spacing.large}>
          {FormCard}
          {FormCardCredit}
          {FormDailyLimit}
          {FormSwitch}
          {cardDetailStatus === "pending" ? <ActivityIndicator color={colors.primary} size="large" /> : <></>}
        </Block>
      </Block>
      {ButtonForm}
      <ModalActivatedCard
        cardID={card?.cardId || ""}
        open={showModalActivated}
        closeModal={() => setShowModalActivated(false)}
        acceptModal={(value: string) => {
          handlerAcceptModalActivated(value);
        }}
      />
    </>
  );
};

export default CardDetail;
